import {useEffect} from 'react'
import {useQuery, useMutation} from 'hooks/graphql'
import {
  CONFIRM_SYNC_HR_MASTER_TASK,
  LATEST_SYNC_HR_MASTER_TASK,
  SYNCED_ORGANIZATION_IDS,
  OrganizationType,
  SyncHrMasterTask,
  LatestSyncHrMasterTaskResult,
  SyncedOrganizationIdsResult,
  SyncedOrganizationIdsVariables,
  LatestSyncHrMasterTaskVariables,
} from './query'

const POLLING_INTERVAL = 3000

interface UseSyncHrMasterProps {
  organizationType: OrganizationType
  additionalDisabledCondition?: () => boolean
}

interface UseSyncHrMasterReturn {
  isSyncing: boolean
  latestSyncTask: SyncHrMasterTask | null
  syncedIds: string[] | null
  loadingLatestTask: boolean
  refetchLatestTask: () => void
  handleCloseBanner: () => void
  disabledForExclusion: string | null
}

export const useSyncHrMaster = ({
  organizationType,
  additionalDisabledCondition,
}: UseSyncHrMasterProps): UseSyncHrMasterReturn => {
  const {
    data: latestSyncHrMasterTaskQueryData,
    loading: loadingLatestTask,
    refetch: refetchLatestTask,
    startPolling,
    stopPolling,
  } = useQuery<LatestSyncHrMasterTaskResult, LatestSyncHrMasterTaskVariables>(LATEST_SYNC_HR_MASTER_TASK, {
    fetchPolicy: 'network-only',
    variables: {organizationType},
  })

  const latestSyncTask = latestSyncHrMasterTaskQueryData?.client.latestSyncHrMasterTask ?? null

  const isSyncing = latestSyncTask !== null && ['waiting', 'in_progress'].includes(latestSyncTask.status)

  useEffect(() => {
    if (isSyncing) {
      startPolling(POLLING_INTERVAL)
    } else {
      stopPolling()
    }
  }, [isSyncing, startPolling, stopPolling])

  const [hideSyncTask] = useMutation(CONFIRM_SYNC_HR_MASTER_TASK)

  const syncedOrganizationIdsQueryResult = useQuery<SyncedOrganizationIdsResult, SyncedOrganizationIdsVariables>(
    SYNCED_ORGANIZATION_IDS,
    {
      fetchPolicy: 'network-only',
      variables: {organizationType},
    }
  )

  const syncedIds = syncedOrganizationIdsQueryResult.data?.client.syncedOrganizationIds ?? null

  const handleCloseBanner = () => {
    if (latestSyncTask?.confirmed == false && ['success', 'failed'].includes(latestSyncTask.status)) {
      hideSyncTask({
        variables: {
          input: {
            id: String(latestSyncTask.id),
          },
        },
        onCompleted: () => {
          refetchLatestTask()
        },
      })
    }
  }

  const disabledForExclusion: string | null = loadingLatestTask
    ? '情報の取得が完了していないため一部操作ができません'
    : isSyncing || additionalDisabledCondition?.()
      ? '情報の一括更新を行っているため一部操作ができません'
      : null

  return {
    isSyncing,
    latestSyncTask,
    syncedIds,
    loadingLatestTask,
    refetchLatestTask,
    handleCloseBanner,
    disabledForExclusion,
  }
}
